/** People with access to a site: invite by email, change role, remove access; plus the activity log. */
import { useState, type FormEvent } from 'react';
import { Layout } from '../components/Layout.js';
import { Dialog, ErrorBox, Loading, useConfirm } from '../components/ui.js';
import { useT } from '../i18n.js';
import { relativeTime } from '../lib/format.js';
import { useAsync, useBackend, useSession, useSite, useToast } from '../state.js';
import { errorText } from './Login.js';

export function People() {
  const { t, lang } = useT();
  const site = useSite();
  const backend = useBackend();
  const { me } = useSession();
  const toast = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const id = site.handle.site.id;
  const members = useAsync(() => backend.members(id), [id]);
  const activity = useAsync(() => backend.activity(id), [id, site.version]);
  const [inviting, setInviting] = useState(false);

  const roleLabel = (r: string) => (r === 'owner' ? t('roleOwner') : t('roleEditor'));

  return (
    <Layout>
      <div className="title-row">
        <div>
          <h1>{t('usersTitle')}</h1>
          <p className="desc">{t('usersIntro', { site: site.handle.site.name })}</p>
        </div>
        <div className="actions">
          <button className="btn" onClick={() => setInviting(true)}>
            {t('invite')}+
          </button>
        </div>
      </div>
      {members.error ? (
        <ErrorBox error={members.error} onRetry={members.reload} />
      ) : !members.data ? (
        <Loading />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('email')}</th>
              <th>{t('role')}</th>
              <th>{t('lastLogin')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members.data.map((m) => (
              <tr key={m.id}>
                <td>
                  <div>{m.name || m.email}</div>
                  {m.name && <div className="muted">{m.email}</div>}
                </td>
                <td>
                  <select
                    className="select"
                    value={m.role}
                    aria-label={`${t('role')} ${m.email}`}
                    disabled={m.id === me?.user.id}
                    onChange={async (e) => {
                      try {
                        await backend.setRole(id, m.id, e.target.value as 'owner' | 'editor');
                        members.reload();
                      } catch (err) {
                        toast(errorText(err, t));
                      }
                    }}
                  >
                    <option value="owner">{roleLabel('owner')}</option>
                    <option value="editor">{roleLabel('editor')}</option>
                  </select>
                </td>
                <td className="muted">{m.last_login_at ? relativeTime(m.last_login_at, t, lang) : `${t('invited')} · ${t('never')}`}</td>
                <td style={{ textAlign: 'right' }}>
                  {m.id !== me?.user.id && (
                    <button
                      className="link-btn danger"
                      onClick={async () => {
                        if (!(await confirm({ title: `${t('removeAccess')}?`, text: t('removeAccessConfirm', { email: m.email, site: site.handle.site.name }), ok: t('removeAccess') }))) return;
                        try {
                          await backend.removeMember(id, m.id);
                          members.reload();
                        } catch (err) {
                          toast(errorText(err, t));
                        }
                      }}
                    >
                      {t('removeAccess')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="hint" style={{ marginTop: 14 }}>
        {t('roleOwner')}: {lang === 'cs' ? 'upravuje obsah a zve další lidi.' : 'edits content and invites people.'} {t('roleEditor')}: {lang === 'cs' ? 'upravuje obsah.' : 'edits content.'}
        {me?.user.studio ? ` ${t('roleStudio')}: ${lang === 'cs' ? 'vidí všechny weby.' : 'sees every site.'}` : ''}
      </p>

      <h2 className="section-h" style={{ marginTop: 40 }}>{t('activity')}</h2>
      {activity.data && activity.data.length > 0 ? (
        <ul className="history" style={{ borderTop: 0 }}>
          {activity.data.slice(0, 30).map((a, i) => {
            let detail: Record<string, unknown> = {};
            try {
              detail = JSON.parse(a.detail);
            } catch {
              /* ignore */
            }
            const what = a.action === 'commit' ? String(detail.message ?? '') : a.action === 'member.invite' ? `${t('invite')}: ${detail.email}` : a.action === 'login' ? t('logIn') : a.action;
            return (
              <li key={i}>
                <span className="msg">{what}</span>
                <span className="by">
                  {a.name || a.email} · {relativeTime(a.created_at, t, lang)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="muted">{t('noRecent')}</p>
      )}
      {inviting && (
        <Invite
          onClose={() => setInviting(false)}
          onDone={(email) => {
            setInviting(false);
            members.reload();
            activity.reload();
            toast(t('inviteSent', { email }));
          }}
        />
      )}
      {confirmDialog}
    </Layout>
  );
}

function Invite({ onClose, onDone }: { onClose: () => void; onDone: (email: string) => void }) {
  const { t } = useT();
  const backend = useBackend();
  const site = useSite();
  const [form, setForm] = useState({ email: '', name: '', role: 'editor' as 'owner' | 'editor' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await backend.invite(site.handle.site.id, form);
      onDone(form.email);
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('inviteTitle')}
      onClose={onClose}
      buttons={
        <>
          <button className="link-btn" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="btn" form="invite" disabled={busy}>
            {t('invite')}
          </button>
        </>
      }
    >
      <form id="invite" className="form-grid" onSubmit={submit}>
        <label>
          {t('email')}
          <input className="input" type="email" required autoComplete="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <label>
          {t('name')}
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <div role="radiogroup" aria-label={t('role')} className="chips">
          {(['editor', 'owner'] as const).map((r) => (
            <button type="button" key={r} role="radio" aria-checked={form.role === r} className={`chip${form.role === r ? ' on' : ''}`} onClick={() => setForm({ ...form, role: r })}>
              {r === 'owner' ? t('roleOwner') : t('roleEditor')}
            </button>
          ))}
        </div>
        <p className="hint" style={{ margin: 0 }}>
          {t('loginIntro')}
        </p>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}
